import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

// Admin -> Viewing (reports db/112, 2026-10-19). Who looks at what on
// reports.nymr.co.uk over the last N days: one line per user (click a name for
// their collated detail), views by user, pages by users, daily activity, time
// of day, and who has access but has not visited. "Insights, not data."

interface UserRow { upn: string; views: number; days_active: number; pages: number; last_seen: string; first_seen: string; last_page: string }
interface PageRow { page: string; users: number; views: number; last_viewed: string }
interface Payload { days: number; logging_since: string | null; total_rows: number; users: UserRow[]; pages: PageRow[]; daily: { day: string; views: number; users: number }[]; hours: { hour: number; views: number }[] }
interface UserDetail {
  upn: string; days: number; total_views: number; usual_hour: number | null
  pages: { page: string; views: number; days_active: number; first_seen: string; last_seen: string }[]
  days_list: { day: string; views: number; items: { page: string; views: number; first_at: string; last_at: string }[] }[]
  hours: { hour: number; views: number }[]
  weekdays: { weekday: number; views: number; days_active: number }[]
}
interface AccessUser { upn: string; name: string; role: string; groups: string[] }

const TH: CSSProperties = { textAlign: 'left', padding: '6px 8px', color: '#6b6b6b', fontWeight: 500, fontSize: '0.78rem', borderBottom: '1px solid #e5e1d6', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#fff' }
const TD: CSSProperties = { padding: '6px 8px', fontSize: '0.8rem', borderBottom: '1px solid #f0ece1', verticalAlign: 'top' }
const NUM: CSSProperties = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }
const GREEN = '#0b3d2e'
const WEEKDAYS = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z')
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })
}
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })
}
function ago(iso: string): string {
  const ms = Date.now() - new Date(iso.endsWith('Z') ? iso : iso + 'Z').getTime()
  const h = Math.floor(ms / 3_600_000)
  if (h < 1) return 'just now'
  if (h < 24) return `${h} h ago`
  const d = Math.floor(h / 24)
  return d === 1 ? 'yesterday' : `${d} days ago`
}
function shortName(upn: string, names: Map<string, string>): string {
  const n = names.get(upn.toLowerCase())
  if (n) return n
  const local = upn.split('@')[0]
  return local.split('.').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')
}

export function AdminViewing() {
  const [days, setDays] = useState(30)
  const [data, setData] = useState<Payload | null>(null)
  const [access, setAccess] = useState<AccessUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [detail, setDetail] = useState<UserDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [v, a] = await Promise.all([fetch(`/api/access/viewing?days=${days}`), fetch('/api/access/users')])
      if (!v.ok) { const b = await v.json().catch(() => ({})); throw new Error(b.error || `Request failed (${v.status})`) }
      setData(await v.json()); setError(null)
      if (a.ok) { const j = await a.json(); setAccess(Array.isArray(j.users) ? j.users : []) }
    } catch (e) { setError(e instanceof Error ? e.message : String(e)) } finally { setLoading(false) }
  }, [days])
  useEffect(() => { void load() }, [load])

  useEffect(() => {
    if (!open) { setDetail(null); return }
    let live = true
    setDetailLoading(true)
    fetch(`/api/access/viewing/user?upn=${encodeURIComponent(open)}&days=${days}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (live) setDetail(d) })
      .finally(() => { if (live) setDetailLoading(false) })
    return () => { live = false }
  }, [open, days])

  const names = useMemo(() => new Map(access.map((u) => [u.upn.toLowerCase(), u.name])), [access])
  const seen = useMemo(() => new Set((data?.users ?? []).map((u) => u.upn.toLowerCase())), [data])
  const notVisited = useMemo(() => access.filter((u) => !seen.has(u.upn.toLowerCase())).sort((a, b) => a.name.localeCompare(b.name)), [access, seen])
  const totals = useMemo(() => {
    const users = data?.users ?? []
    const views = users.reduce((a, u) => a + u.views, 0)
    return { users: users.length, views, pages: (data?.pages ?? []).length, perUser: users.length ? Math.round(views / users.length) : 0 }
  }, [data])
  const byUser = useMemo(() => (data?.users ?? []).slice().sort((a, b) => b.views - a.views).map((u) => ({ name: shortName(u.upn, names), views: u.views, days: u.days_active })), [data, names])
  const byPage = useMemo(() => (data?.pages ?? []).map((p) => ({ page: p.page, users: p.users, views: p.views })), [data])
  const hours = useMemo(() => Array.from({ length: 24 }, (_, h) => ({ hour: `${String(h).padStart(2, '0')}`, views: data?.hours.find((x) => x.hour === h)?.views ?? 0 })), [data])

  const tile = (label: string, value: string, note?: string) => (
    <div style={{ minWidth: 150, padding: '8px 14px', border: '1px solid #e5e1d6', borderRadius: 8, background: '#fff' }}>
      <div style={{ fontSize: '0.7rem', color: '#6b6b6b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: GREEN, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {note && <div style={{ fontSize: '0.7rem', color: '#8a8577' }}>{note}</div>}
    </div>
  )
  const section = (title: string, children: React.ReactNode, note?: string) => (
    <section style={{ marginTop: 18 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: '0.95rem', color: GREEN }}>{title}</h3>
      {note && <p style={{ margin: '0 0 8px', fontSize: '0.75rem', color: '#8a8577' }}>{note}</p>}
      {children}
    </section>
  )
  const short = (iso: string | null) => (iso ? `since ${fmtDate(iso.slice(0, 10))}` : '')
  const coverageNote = data?.logging_since && (Date.now() - new Date(data.logging_since + 'Z').getTime()) / 86_400_000 < days
    ? `Logging started ${fmtDateTime(data.logging_since)}, so this window is shorter than ${days} days.` : undefined

  return (
    <>
      <header className="events-admin__head">
        <p className="admin-subtle" style={{ marginTop: 0, marginBottom: 0 }}>
          Who looks at what on reports.nymr.co.uk. A view is one visit to a tab (or a sub-view such as Customer Service › Comps); repeats within a minute are not counted. Times are UK local.
          {coverageNote && <> <strong>{coverageNote}</strong></>}
        </p>
        <label style={{ fontSize: '0.8rem', color: '#6b6b6b', display: 'flex', alignItems: 'center', gap: 6 }}>
          Last
          <select value={days} onChange={(e) => { setOpen(null); setDays(Number(e.target.value)) }} style={{ padding: '4px 8px', fontSize: '0.85rem', border: '1px solid #ccc', borderRadius: 6, background: '#fff' }} aria-label="Window">
            <option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option>
          </select>
        </label>
      </header>

      {loading && <p>Loading…</p>}
      {error && <p style={{ color: '#a33' }}>Couldn&apos;t load: {error}</p>}
      {data && !loading && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '10px 0' }}>
            {tile('Active users', String(totals.users), `of ${access.length || '—'} with access`)}
            {tile('Page views', totals.views.toLocaleString('en-GB'), `${totals.perUser} per user`)}
            {tile('Pages used', String(totals.pages))}
            {tile('Not visited', String(notVisited.length), 'have access, no views in the window')}
          </div>

          {section('Users — most recent first', (
            <div style={{ overflowX: 'auto', maxHeight: '60vh', overflowY: 'auto', border: '1px solid #e5e1d6', borderRadius: 8, background: '#fff' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
                <thead><tr>
                  <th style={TH}>User</th><th style={TH}>Last seen</th><th style={TH}>Last page</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Views</th><th style={{ ...TH, textAlign: 'right' }}>Days active</th><th style={{ ...TH, textAlign: 'right' }}>Pages</th><th style={TH}>First seen</th>
                </tr></thead>
                <tbody>
                  {data.users.map((u) => {
                    const isOpen = open === u.upn
                    return (
                      <UserRows key={u.upn} u={u} name={shortName(u.upn, names)} isOpen={isOpen} detail={isOpen ? detail : null} loading={isOpen && detailLoading} onToggle={() => setOpen(isOpen ? null : u.upn)} />
                    )
                  })}
                  {data.users.length === 0 && <tr><td style={TD} colSpan={7}>No page views recorded in the last {days} days.</td></tr>}
                </tbody>
              </table>
            </div>
          ), 'Click a name for what they look at, by day, collated.')}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 18 }}>
            {section('Views by user', (
              <div style={{ height: Math.max(160, 26 * byUser.length + 40), background: '#fff', border: '1px solid #e5e1d6', borderRadius: 8, padding: 8 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byUser} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} interval={0} />
                    <Tooltip formatter={(v: number, k: string) => [v, k === 'views' ? 'Views' : 'Days active']} />
                    <Bar dataKey="views" fill={GREEN} name="views" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ), 'Ranked by total page views in the window.')}

            {section('Pages by number of users', (
              <div style={{ height: Math.max(160, 26 * byPage.length + 40), background: '#fff', border: '1px solid #e5e1d6', borderRadius: 8, padding: 8 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byPage} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="page" width={190} tick={{ fontSize: 11 }} interval={0} />
                    <Tooltip formatter={(v: number, k: string) => [v, k === 'users' ? 'Users' : 'Views']} />
                    <Legend />
                    <Bar dataKey="users" fill={GREEN} name="users" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="views" fill="#b9a46a" name="views" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ), 'Which tabs reach the most people (distinct users), with total views alongside.')}

            {section('Daily activity', (
              <div style={{ height: 220, background: '#fff', border: '1px solid #e5e1d6', borderRadius: 8, padding: 8 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.daily.map((d) => ({ ...d, label: fmtDate(d.day) }))} margin={{ left: 0, right: 16, top: 8, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="users" name="Active users" stroke={GREEN} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="views" name="Views" stroke="#b9a46a" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ), 'Active users and views per day.')}

            {section('Time of day', (
              <div style={{ height: 220, background: '#fff', border: '1px solid #e5e1d6', borderRadius: 8, padding: 8 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hours} margin={{ left: 0, right: 16, top: 8, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={1} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="views" name="Views" fill={GREEN} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ), 'When the site is used, by hour (UK time).')}
          </div>

          {section('Has access but has not visited', notVisited.length === 0 ? <p style={{ fontSize: '0.8rem', color: '#8a8577' }}>Everyone with access has visited in the window.</p> : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {notVisited.map((u) => <span key={u.upn} title={u.upn} style={{ padding: '2px 10px', borderRadius: 10, background: '#fdf1dc', color: '#8a5a00', fontSize: '0.78rem' }}>{u.name} <span style={{ opacity: 0.7 }}>({u.role})</span></span>)}
            </div>
          ), `Members of the Reports groups with no page views in the last ${days} days ${short(data.logging_since)}.`)}
        </>
      )}
    </>
  )
}

function UserRows({ u, name, isOpen, detail, loading, onToggle }: { u: UserRow; name: string; isOpen: boolean; detail: UserDetail | null; loading: boolean; onToggle: () => void }) {
  return (
    <>
      <tr onClick={onToggle} style={{ cursor: 'pointer', background: isOpen ? '#eaf2ee' : undefined }} aria-expanded={isOpen}>
        <td style={{ ...TD, whiteSpace: 'nowrap', fontWeight: 600, color: GREEN }}>{isOpen ? '▾ ' : '▸ '}{name}<div style={{ fontSize: '0.7rem', color: '#8a8577', fontWeight: 400 }}>{u.upn}</div></td>
        <td style={{ ...TD, whiteSpace: 'nowrap' }}>{fmtDateTime(u.last_seen)}<div style={{ fontSize: '0.7rem', color: '#8a8577' }}>{ago(u.last_seen)}</div></td>
        <td style={TD}>{u.last_page}</td>
        <td style={{ ...TD, ...NUM }}>{u.views}</td>
        <td style={{ ...TD, ...NUM }}>{u.days_active}</td>
        <td style={{ ...TD, ...NUM }}>{u.pages}</td>
        <td style={{ ...TD, whiteSpace: 'nowrap' }}>{fmtDateTime(u.first_seen)}</td>
      </tr>
      {isOpen && (
        <tr><td colSpan={7} style={{ padding: '6px 10px 14px 28px', background: '#f7f9f7', borderBottom: '1px solid #e5e1d6' }}>
          {loading && <p style={{ margin: 0, fontSize: '0.8rem' }}>Loading…</p>}
          {detail && (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) minmax(360px, 2fr)', gap: 18 }}>
              <div>
                <div style={{ fontSize: '0.78rem', color: '#6b6b6b', marginBottom: 4 }}>
                  <strong style={{ color: GREEN }}>{detail.total_views} views</strong> over {detail.days_list.length} day{detail.days_list.length === 1 ? '' : 's'}
                  {detail.usual_hour != null && <> · usually around <strong>{String(detail.usual_hour).padStart(2, '0')}:00</strong></>}
                  {detail.weekdays.length > 0 && <> · busiest day <strong>{WEEKDAYS[detail.weekdays.slice().sort((a, b) => b.views - a.views)[0].weekday]}</strong></>}
                </div>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead><tr><th style={{ ...TH, position: 'static', background: 'transparent' }}>Page</th><th style={{ ...TH, position: 'static', background: 'transparent', textAlign: 'right' }}>Views</th><th style={{ ...TH, position: 'static', background: 'transparent', textAlign: 'right' }}>Days</th><th style={{ ...TH, position: 'static', background: 'transparent' }}>Last</th></tr></thead>
                  <tbody>
                    {detail.pages.map((p) => (
                      <tr key={p.page}><td style={TD}>{p.page}</td><td style={{ ...TD, ...NUM }}>{p.views}</td><td style={{ ...TD, ...NUM }}>{p.days_active}</td><td style={{ ...TD, whiteSpace: 'nowrap' }}>{fmtDateTime(p.last_seen)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <div style={{ fontSize: '0.78rem', color: '#6b6b6b', marginBottom: 4 }}>By day — pages with view counts and the time span</div>
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {detail.days_list.map((d) => (
                    <div key={d.day} style={{ display: 'flex', gap: 10, fontSize: '0.78rem', padding: '3px 0', borderBottom: '1px solid #ece8dd' }}>
                      <div style={{ minWidth: 110, whiteSpace: 'nowrap', fontWeight: 600, color: GREEN }}>{fmtDate(d.day)}</div>
                      <div style={{ minWidth: 34, textAlign: 'right', color: '#6b6b6b' }}>{d.views}</div>
                      <div>{d.items.map((i) => `${i.page} ×${i.views} (${i.first_at === i.last_at ? i.first_at : `${i.first_at}–${i.last_at}`})`).join(' · ')}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </td></tr>
      )}
    </>
  )
}
